import CartItem from "@models/CartItem.js";
import Vue from "vue";
import { uuid } from "vue-uuid";
import { createVuexHelpers } from "@store_modules/utils/vuexHelpers";
import { EventBus } from "@/plugins/global-events";
import helper from "../../../components/mixins/helper.js";
import {
    BuildCartKey,
    BuildMediaTagEntries,
    GetAlbumKeyId,
    NormalizeCartTags,
} from "@helpers/cartContractHelper.js";
import { IsDateYmd } from "@helpers/dateHelper";

export default {
    namespaced: true,
    state: {},
    mutations: {},
    actions: {
        /**
         * Adds or removes one individual media item from the cart.
         *
         * The album visit date is preserved for the sale/expiration while each
         * selected media keeps its own visit date for RFID association.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} payload Selected media and album context.
         * @returns {Promise<void>}
         */
        async addCartByIndividual(
            context,
            { item, album, invoiceId = null }
        ) {
            const helpers = createVuexHelpers(context);
            const previousMediaIds = Array.isArray(album?.MediaIds)
                ? [...album.MediaIds]
                : [];
            const previousSelectedMedia = Array.isArray(album?.SelectedMedia)
                ? album.SelectedMedia.map((media) => ({ ...media }))
                : [];
            const previousIsInCart = Boolean(item?.IsInCart);

            try {
                await helpers.dispatchGlobal("setLoading", true);

                const parkId = Number(album.Park.Id);
                const parkPrefix = helper.methods.getParkPrefix(parkId);
                const albumKeyId = GetAlbumKeyId(album);
                const cartKey = BuildCartKey({
                    kind: "individual",
                    parkId,
                    albumKeyId,
                });
                const selectedMedia = [...previousSelectedMedia];
                const selectedIndex = selectedMedia.findIndex(
                    (media) => Number(media.MediaId) === Number(item.MediaId)
                );

                if (selectedIndex >= 0) {
                    selectedMedia.splice(selectedIndex, 1);
                    Vue.set(item, "IsInCart", false);
                } else {
                    const mediaVisitDate = String(item.VisitDate || "").slice(0, 10);

                    if (!IsDateYmd(mediaVisitDate)) {
                        throw new Error(
                            "The selected media does not contain a valid visit date."
                        );
                    }

                    selectedMedia.push({
                        MediaId: Number(item.MediaId),
                        VisitDate: mediaVisitDate,
                        Tags: NormalizeCartTags(item.Tags),
                    });
                    Vue.set(item, "IsInCart", true);
                }

                const mediaIds = selectedMedia.map((media) => media.MediaId);
                Vue.set(album, "MediaIds", mediaIds);
                Vue.set(album, "SelectedMedia", selectedMedia);

                if (!mediaIds.length) {
                    helpers.commitGlobal("REMOVE_CART_ITEM", cartKey);
                } else {
                    const saleVisitDate = String(album?.VisitDate || "").slice(0, 10);

                    if (!IsDateYmd(saleVisitDate)) {
                        throw new Error(
                            "The album does not contain a valid sale visit date."
                        );
                    }

                    const apiKohTags = BuildMediaTagEntries(
                        selectedMedia,
                        parkId,
                        parkPrefix,
                        0
                    ).map(({ tag, visitDate }) => ({
                        tag,
                        visitDate,
                    }));

                    if (!apiKohTags.length) {
                        throw new Error(
                            "The selected media does not contain a valid tag and visit date."
                        );
                    }

                    const cartItem = new CartItem({
                        id: uuid.v4(),
                        cantidad: mediaIds.length,
                        Pax: mediaIds.length,
                        ParkId: parkId,
                        type: 1,
                        TotalMedia: mediaIds.length,
                        VisitDate: saleVisitDate,
                        tags: apiKohTags,
                    });
                    cartItem.CartKey = cartKey;
                    cartItem.AlbumKeyId = albumKeyId;

                    const pricedItem = await helpers.dispatchGlobal(
                        "GetNewPrice",
                        cartItem
                    );

                    if (!pricedItem?.RateKey || pricedItem?.Id === undefined) {
                        throw new Error(
                            "The individual-media quotation response is incomplete."
                        );
                    }

                    pricedItem.CartKey = cartKey;
                    pricedItem.AlbumKeyId = albumKeyId;
                    pricedItem.invoiceId = invoiceId;
                    pricedItem.tags = apiKohTags;
                    pricedItem.VisitDate = saleVisitDate;
                    pricedItem.visitDate = saleVisitDate;
                    pricedItem.MediaCollection = mediaIds;
                    pricedItem.SelectedMedia = selectedMedia;
                    pricedItem.AlbumMode = album.AlbumMode;
                    pricedItem.ParkPrefix = parkPrefix;
                    pricedItem.isEvent = false;

                    helpers.commitGlobal("setCarrito", pricedItem);
                }

                await helpers.dispatchGlobal("SynchronizeAlbumCartState");
                await helpers.dispatchGlobal("addShoppingCartAmount");
                await helpers.dispatchGlobal("getAmount");
                await helpers.dispatchGlobal("updateAlbumComponent");
                await helpers.dispatchGlobal("updateShoppingCartComponent");
            } catch (error) {
                Vue.set(album, "MediaIds", previousMediaIds);
                Vue.set(album, "SelectedMedia", previousSelectedMedia);
                Vue.set(item, "IsInCart", previousIsInCart);
                await helpers.dispatchGlobal("SynchronizeAlbumCartState");
                throw error;
            } finally {
                await helpers.dispatchGlobal("setLoading", false);
            }
        },

        /**
         * Removes one individual-media cart product.
         *
         * @param {Object} context Vuex action context.
         * @param {Object} payload Removal identifiers.
         * @returns {Promise<void>}
         */
        async removeItemFromCardByIndividual(
            context,
            { parkId, cartKey = null, albumKeyId = null }
        ) {
            const { rootState } = context;
            const helpers = createVuexHelpers(context);

            const resolvedCartKey = cartKey || Object.keys(
                rootState.shoppingCart || {}
            ).find((key) => {
                const cartItem = rootState.shoppingCart[key];

                if (albumKeyId) {
                    return String(cartItem?.AlbumKeyId || "") ===
                        String(albumKeyId);
                }

                return (
                    Number(cartItem?.type) === 1 &&
                    Number(cartItem?.AlbumMode) === 1 &&
                    Number(cartItem?.ParkId) === Number(parkId)
                );
            });

            if (resolvedCartKey !== undefined && resolvedCartKey !== null) {
                helpers.commitGlobal("REMOVE_CART_ITEM", resolvedCartKey);
            }

            for (const album of Array.isArray(rootState.albumes)
                ? rootState.albumes
                : []) {
                for (const park of Array.isArray(album?.park) ? album.park : []) {
                    const matchesAlbumKey = albumKeyId &&
                        GetAlbumKeyId(park) === String(albumKeyId);
                    const matchesLegacyPark = !albumKeyId &&
                        Number(park?.Park?.Id) === Number(parkId) &&
                        Number(park?.AlbumMode) === 1;

                    if (!matchesAlbumKey && !matchesLegacyPark) {
                        continue;
                    }

                    Vue.set(park, "IsAdded", false);
                    Vue.set(park, "MediaIds", []);
                    Vue.set(park, "SelectedMedia", []);
                }
            }

            EventBus.$emit("updateGalleryItems", {
                source: "RemoveItemFromCartByIndividual",
                update: true,
            });

            await helpers.dispatchGlobal("SynchronizeAlbumCartState");
            await helpers.dispatchGlobal("refreshShoppingCart");
        },

        /**
         * Clears temporary cart indicators from gallery items.
         *
         * @param {Object} context Vuex action context.
         * @param {Array<Object>} gallery Gallery media items.
         * @returns {void}
         */
        resetGalleryItems(context, gallery) {
            for (const item of Array.isArray(gallery) ? gallery : []) {
                if (Object.prototype.hasOwnProperty.call(item, "IsAdded")) {
                    Vue.delete(item, "IsAdded");
                }
                if (Object.prototype.hasOwnProperty.call(item, "IsInCart")) {
                    Vue.delete(item, "IsInCart");
                }
            }
        },
    },
};
